import { ReactNode, useEffect, useState } from 'react';

import { Popover } from '@mui/material';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useMatch } from 'react-router-dom';

import {
  Badge,
  Button,
  Divider,
  Icons,
  ShadowDivider,
  tokens,
  useDrawer,
  useDrawerInstance,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

import { Link } from 'components/router';
import { useAbility } from '@cast/shared-ability-data-access';
import { ACTIVITY_PATHS, BASE_PATHS } from '@cast/console-constants';
import {
  useGetNotificationsQuery,
  useGetSlackWorkspaces,
  useGetSlackConfigurations,
  useGetWebhooksQuery,
} from 'hooks/queries/notifications';
import { useNavigate } from '@cast/infrastructure-organization';
import { HeaderIconButton } from 'main-layout/header/HeaderIconButton';

import { NoNotifications, NotificationRow, SlackBanner } from './_components';
import {
  CREATE_ALERT_ACTION_KEY,
  CREATE_ALERT_ACTION_VALUE,
  MANAGE_ALERTS_ACTION_VALUE,
} from '../_hooks/useNavigateToCreateAlert';
import { AddAlertDrawer, AlertsDrawer } from '../alerts-drawer';
import { useNotificationDrawer } from '../hooks/useNotificationDrawer';

const SLACK_BANNER_DISMISSED_KEY = 'notifications-slack-banner-dismissed';

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const activityNotificationsMatch = useMatch(
    `${ACTIVITY_PATHS.activityNotifications}/*`
  );
  const legacyNotificationsMatch = useMatch(`${BASE_PATHS.notifications}/*`);
  const isInNotifications =
    !!activityNotificationsMatch || !!legacyNotificationsMatch;
  const { openNotificationDrawer } = useNotificationDrawer();
  const ability = useAbility();
  const notificationDrawerRef = useDrawerInstance();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = !!anchorEl && !notificationDrawerRef.id;
  const [isSlackBannerDismissed, setIsSlackBannerDismissed] = useState(true);

  const canCreateAlert = ability.can('create', 'OrganizationWebHooks');
  const { open: openDrawer } = useDrawer();

  const { data: slackWorkspacesData } = useGetSlackWorkspaces();
  const hasConnectedSlackWorkspace =
    (slackWorkspacesData?.items?.length ?? 0) > 0;
  const { webhooks } = useGetWebhooksQuery({ enabled: canCreateAlert });
  const { data: slackConfigurations } =
    useGetSlackConfigurations(canCreateAlert);

  const hasAlerts =
    (webhooks?.length ?? 0) > 0 ||
    (slackConfigurations?.items?.length ?? 0) > 0;

  const handleSetupAlerts = () => {
    setAnchorEl(null);
    if (isInNotifications) {
      if (hasAlerts) {
        openDrawer({ component: AlertsDrawer, props: {} });
      } else {
        openDrawer({ component: AddAlertDrawer, props: {} });
      }
    } else {
      const actionValue = hasAlerts
        ? MANAGE_ALERTS_ACTION_VALUE
        : CREATE_ALERT_ACTION_VALUE;
      navigate(
        `${ACTIVITY_PATHS.activityNotifications}?${CREATE_ALERT_ACTION_KEY}=${actionValue}`
      );
    }
  };

  // Load banner dismissed state from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem(SLACK_BANNER_DISMISSED_KEY);
    setIsSlackBannerDismissed(dismissed === 'true');
  }, []);

  const handleCloseBanner = () => {
    localStorage.setItem(SLACK_BANNER_DISMISSED_KEY, 'true');
    setIsSlackBannerDismissed(true);
  };

  const handleConnectSlack = () => {
    setAnchorEl(null);
    if (isInNotifications) {
      openDrawer({ component: AddAlertDrawer, props: {} });
    } else {
      navigate(
        `${ACTIVITY_PATHS.activityNotifications}?${CREATE_ALERT_ACTION_KEY}=${CREATE_ALERT_ACTION_VALUE}`
      );
    }
  };

  const {
    notificationsResponse,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    error,
  } = useGetNotificationsQuery({
    filter: { onlyUnresolved: true, isExpired: false },
    refetch: !isOpen,
  });

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: fetchNextPage,
    rootMargin: '0px 0px 200px 0px',
  });

  const unseen = notificationsResponse.countUnacked;
  const hasAny = notificationsResponse.hasAny;
  const notifications = notificationsResponse.items;

  if (error) {
    return null;
  }

  let content: ReactNode;

  if (isLoading) {
    content = <NotificationRow isLoading />;
  } else {
    if (!hasAny) {
      content = (
        <Stack direction="row" justifyContent="center" width="100%">
          <NoNotifications sx={{ width: '177px', pt: '20px', pb: '40px' }} />
        </Stack>
      );
    } else {
      if (hasAny && unseen === 0) {
        content = (
          <Stack direction="row" justifyContent="center" width="100%">
            <NoNotifications
              allResolved
              sx={{ width: '240px', pt: '20px', pb: '40px' }}
            />
          </Stack>
        );
      } else {
        content = (
          <Stack>
            {notifications.map((notification, index) => (
              <NotificationRow
                onClick={() => {
                  openNotificationDrawer({
                    notification,
                  });
                }}
                key={notification.id || index}
                notification={notification as any}
              />
            ))}
            <div ref={infiniteRef}>
              {isFetching && (
                <>
                  <ShadowDivider
                    opacity={0.6}
                    direction="bottom"
                    orientation="vertical"
                  />
                  <NotificationRow isLoading />
                </>
              )}
            </div>
          </Stack>
        );
      }
    }
  }

  return (
    <>
      <HeaderIconButton
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
        active={isOpen}
        testId="notifications-header-menu-btn"
      >
        {unseen ? (
          <Badge
            count={unseen}
            horizontal="right"
            size="small"
            variant="primary"
            vertical="top"
          >
            <Icons.BellSimple size={16} weight="fill" color="currentColor" />
          </Badge>
        ) : (
          <Icons.BellSimple size={16} weight="fill" color="currentColor" />
        )}
      </HeaderIconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: '7px',
              zIndex: (theme) => theme.zIndex.drawer - 1,
              boxShadow: tokens.theme.shadows.elevation.medium,
              borderRadius: '4px',
              backgroundColor: tokens.theme.bg.structural.surface,
            },
          },
        }}
        disableScrollLock
      >
        <Stack
          width={360}
          maxHeight={500}
          data-testid="notifications-header-menu"
        >
          <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
            py={8}
            px={12}
          >
            <Typography
              variant="P14B"
              color={tokens.theme.fg.structural.primary}
            >
              Notifications
            </Typography>
            <Stack direction="row" alignItems="center" gap={4}>
              {canCreateAlert && (
                <Button
                  variant="text"
                  size="md"
                  startIcon={<Icons.Gear size={16} />}
                  onClick={handleSetupAlerts}
                  testId="setup-alerts-button"
                />
              )}
              <Link
                to={ACTIVITY_PATHS.activityNotifications}
                style={{
                  textDecoration: 'none',
                  display: isInNotifications ? 'none' : undefined,
                }}
                onClick={() => setAnchorEl(null)}
              >
                <Button
                  variant="text"
                  size="md"
                  endIcon={<Icons.ArrowRight size={16} />}
                >
                  View all
                </Button>
              </Link>
            </Stack>
          </Stack>
          <Box
            flex={1}
            sx={{
              overflowY: 'auto',
            }}
            ref={rootRef}
          >
            <Divider />
            {content}
          </Box>
          {canCreateAlert &&
            !isSlackBannerDismissed &&
            !hasConnectedSlackWorkspace && (
              <SlackBanner
                onClose={handleCloseBanner}
                onConnectSlack={handleConnectSlack}
              />
            )}
        </Stack>
      </Popover>
    </>
  );
};
